import { Box, Typography } from '@mui/material';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import Markdown from 'react-markdown';
import type { Components } from 'react-markdown';

import type { AskTurn } from '../types/syntiro-agent';
import AskSyntiroLoadingDots from './AskSyntiroLoadingDots';
import {
  SKY,
  SPARK_GRADIENT,
  TEXT_ANSWER,
  TEXT_MUTED,
  TEXT_PRIMARY,
  USER_BUBBLE_BG,
  USER_BUBBLE_BORDER
} from './askSyntiroTheme';

const agentTextSx = { fontSize: 14.5, lineHeight: 1.7, color: TEXT_ANSWER } as const;

const agentMarkdownComponents: Components = {
  p: ({ children }) => (
    <Typography
      component="p"
      sx={{ ...agentTextSx, textAlign: 'left', m: 0, mb: 1.5, '&:last-child': { mb: 0 } }}
    >
      {children}
    </Typography>
  ),
  strong: ({ children }) => (
    <Box component="strong" sx={{ fontWeight: 700 }}>
      {children}
    </Box>
  ),
  em: ({ children }) => <Box component="em">{children}</Box>,
  ul: ({ children }) => (
    <Box component="ul" sx={{ textAlign: 'left', pl: 3, m: 0, mb: 1.5 }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ textAlign: 'left', pl: 3, m: 0, mb: 1.5 }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Box component="li" sx={{ ...agentTextSx, textAlign: 'left', mb: 0.5 }}>
      {children}
    </Box>
  )
};

export interface AskSyntiroTurnProps {
  turn: AskTurn;
}

function AgentSenderLabel() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Box sx={{ width: 18, height: 18, borderRadius: '6px', background: SPARK_GRADIENT }} />
      <Typography
        component="span"
        sx={{
          fontSize: 12,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
          color: SKY
        }}
      >
        Syntiro
      </Typography>
    </Box>
  );
}

/**
 * One user, agent or tool turn. The user's own text renders plain and
 * whitespace-preserving, and a tool turn renders as a muted one-line note.
 * The agent's reply renders through react-markdown, which builds React
 * elements from the parsed markdown — never `dangerouslySetInnerHTML` — and
 * does not interpret raw HTML found in the text unless a `rehype-raw`-style
 * plugin is added, which it isn't. A hostile tag in the model's reply lands
 * as inert text, not markup.
 */
export default function AskSyntiroTurn({ turn }: AskSyntiroTurnProps) {
  if (turn.author === 'tool') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 3, pr: 2, mb: 2 }}>
        <BuildOutlinedIcon sx={{ fontSize: 15, color: TEXT_MUTED, flexShrink: 0 }} />
        <Typography component="span" sx={{ textAlign: 'left', fontSize: 14, color: TEXT_ANSWER }}>
          {turn.text}
        </Typography>
      </Box>
    );
  }

  if (turn.author === 'user') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, mb: 4 }}>
        <Box
          sx={{
            maxWidth: '80%',
            backgroundColor: USER_BUBBLE_BG,
            border: '1px solid',
            borderColor: USER_BUBBLE_BORDER,
            borderRadius: '14px 14px 4px 14px',
            px: 1.75,
            py: 1.125
          }}
        >
          <Typography
            component="p"
            sx={{ whiteSpace: 'pre-wrap', textAlign: 'left', fontSize: 14, color: TEXT_PRIMARY }}
          >
            {turn.text}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ pl: 3, pr: 2, mb: 4 }}>
      <AgentSenderLabel />
      <Markdown skipHtml components={agentMarkdownComponents}>
        {turn.text}
      </Markdown>
    </Box>
  );
}

export interface AskSyntiroPendingTurnProps {
  elapsedSeconds: number;
}

export function AskSyntiroPendingTurn({ elapsedSeconds }: AskSyntiroPendingTurnProps) {
  return (
    <Box sx={{ pl: 3, pr: 2, mb: 4 }}>
      <AgentSenderLabel />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AskSyntiroLoadingDots />
        <Typography component="span" sx={{ textAlign: 'left', fontSize: 12.5, color: TEXT_MUTED }}>
          Thinking
          <Box component="span" aria-hidden="true">
            {' '}
            ({elapsedSeconds}s)
          </Box>
        </Typography>
      </Box>
    </Box>
  );
}
